import { type BlockedPlayer, BlockRejectedSchema, BlocksStateSchema } from '@shefira/shared';

import type { ChatPanel } from './chat.js';
import { BLOCK_REFUSAL_TEXT } from './social-text.js';
import { uiFormat } from './text.js';

/** The little of a Colyseus room this needs. */
interface BlockRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/**
 * The players this character blocked (ADR 0076), as the server last sent
 * them. The server already stops their private lines and invitations; what
 * is left to the client is hiding their lines on the public channels and
 * over their heads, which `isBlocked` answers by name.
 */
export class BlockClient {
  private blocked: BlockedPlayer[] = [];
  private names = new Set<string>();
  /** The first list is the one loaded on arrival: nothing to tell then. */
  private loaded = false;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly room: BlockRoom,
    chat: ChatPanel,
  ) {
    room.onMessage('blocks', (payload) => {
      const parsed = BlocksStateSchema.safeParse(payload);
      if (!parsed.success) return;
      const before = new Map(
        this.blocked.map((player) => [player.name.toLowerCase(), player.name]),
      );
      this.blocked = parsed.data.blocked;
      this.names = new Set(this.blocked.map((player) => player.name.toLowerCase()));
      if (this.loaded) {
        for (const player of this.blocked) {
          if (!before.has(player.name.toLowerCase())) {
            chat.notice(
              uiFormat('{name} est bloqué : tu ne verras plus ses messages ni ses invitations.', {
                name: player.name,
              }),
            );
          }
        }
        for (const [key, name] of before) {
          if (!this.names.has(key)) chat.notice(uiFormat('{name} est débloqué.', { name }));
        }
      }
      this.loaded = true;
      for (const listener of this.listeners) listener();
    });
    room.onMessage('block_rejected', (payload) => {
      const parsed = BlockRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(BLOCK_REFUSAL_TEXT[parsed.data.reason]);
    });
  }

  get list(): readonly BlockedPlayer[] {
    return this.blocked;
  }

  isBlocked(name: string): boolean {
    return this.names.has(name.toLowerCase());
  }

  block(name: string): void {
    this.room.send('block_player', { name });
  }

  /** By name, as typed or as listed; false when they are not blocked. */
  unblock(name: string): boolean {
    const wanted = name.trim().toLowerCase();
    const player = this.blocked.find((blocked) => blocked.name.toLowerCase() === wanted);
    if (player === undefined) return false;
    this.room.send('unblock_player', { characterId: player.id });
    return true;
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
